// Centralized Function Registry System
// This system enables function renaming with automatic reflection across the entire codebase

export interface FunctionDefinition {
    id: string;
    name: string;
    displayName: string;
    description: string;
    category: string;
    isCustom: boolean;
    parameters: FunctionParameter[];
    implementation: (...args: any[]) => any;
    aliases?: string[]; // For backward compatibility
}

export interface FunctionParameter {
    name: string;
    type: 'string' | 'number' | 'boolean' | 'object' | 'array' | 'select' | 'textarea';
    description?: string;
    required: boolean;
    defaultValue?: any;
    options?: string[]; // For select type
}

export interface FunctionRegistry {
    functions: Map<string, FunctionDefinition>;
    nameToId: Map<string, string>;
    aliases: Map<string, string>;
}

class FunctionRegistryManager {
    private registry: FunctionRegistry = {
        functions: new Map(),
        nameToId: new Map(),
        aliases: new Map()
    };

    private listeners: Set<(registry: FunctionRegistry) => void> = new Set();

    // Register a function with the registry
    registerFunction(definition: FunctionDefinition): void {
        // Validate function definition
        if (!definition.id || !definition.name || !definition.implementation) {
            throw new Error('Invalid function definition: missing required fields');
        }

        // Check for name conflicts
        if (this.registry.nameToId.has(definition.name) && 
            this.registry.nameToId.get(definition.name) !== definition.id) {
            throw new Error(`Function name "${definition.name}" is already registered`);
        }

        // Store the function
        this.registry.functions.set(definition.id, definition);
        this.registry.nameToId.set(definition.name, definition.id);

        // Register aliases for backward compatibility
        if (definition.aliases) {
            definition.aliases.forEach(alias => {
                this.registry.aliases.set(alias, definition.id);
            });
        }

        // Notify listeners
        this.notifyListeners();
    }

    // Unregister a function
    unregisterFunction(id: string): void {
        const functionDef = this.registry.functions.get(id);
        if (!functionDef) return;

        // Remove from all maps
        this.registry.functions.delete(id);
        this.registry.nameToId.delete(functionDef.name);

        // Remove aliases
        if (functionDef.aliases) {
            functionDef.aliases.forEach(alias => {
                this.registry.aliases.delete(alias);
            });
        }

        // Notify listeners
        this.notifyListeners();
    }

    // Rename a function
    renameFunction(id: string, newName: string): void {
        const functionDef = this.registry.functions.get(id);
        if (!functionDef) {
            throw new Error(`Function with id "${id}" not found`);
        }

        // Check if new name conflicts
        if (this.registry.nameToId.has(newName) && 
            this.registry.nameToId.get(newName) !== id) {
            throw new Error(`Function name "${newName}" is already in use`);
        }

        const oldName = functionDef.name;
        
        // Update the function definition
        functionDef.name = newName;
        this.registry.functions.set(id, functionDef);

        // Update name mapping
        this.registry.nameToId.delete(oldName);
        this.registry.nameToId.set(newName, id);

        // Notify listeners
        this.notifyListeners();
    }

    // Get function by ID
    getFunction(id: string): FunctionDefinition | undefined {
        return this.registry.functions.get(id);
    }

    // Get function by name (including aliases)
    getFunctionByName(name: string): FunctionDefinition | undefined {
        const id = this.registry.nameToId.get(name) || this.registry.aliases.get(name);
        return id ? this.registry.functions.get(id) : undefined;
    }

    // Get all functions
    getAllFunctions(): FunctionDefinition[] {
        return Array.from(this.registry.functions.values());
    }

    // Get functions by category
    getFunctionsByCategory(category: string): FunctionDefinition[] {
        return this.getAllFunctions().filter(func => func.category === category);
    }

    // Execute a function by name or ID
    async executeFunction(identifier: string, params: any[]): Promise<any> {
        const functionDef = this.getFunction(identifier) || this.getFunctionByName(identifier);
        if (!functionDef) {
            throw new Error(`Function "${identifier}" not found`);
        }

        const startTime = performance.now();
        
        try {
            const result = await functionDef.implementation(...params);
            const duration = performance.now() - startTime;
            
            return {
                success: true,
                message: `Successfully executed ${functionDef.name}`,
                duration,
                result
            };
        } catch (error) {
            const duration = performance.now() - startTime;
            return {
                success: false,
                message: `Failed to execute ${functionDef.name}: ${error}`,
                duration,
                error
            };
        }
    }

    // Subscribe to registry changes
    subscribe(listener: (registry: FunctionRegistry) => void): () => void {
        this.listeners.add(listener);
        
        // Return unsubscribe function
        return () => {
            this.listeners.delete(listener);
        };
    }

    // Notify all listeners of registry changes
    private notifyListeners(): void {
        this.listeners.forEach(listener => {
            try {
                listener(this.registry);
            } catch (error) {
                console.error('Error in registry listener:', error);
            }
        });
    }

    // Get registry snapshot
    getRegistry(): FunctionRegistry {
        return {
            functions: new Map(this.registry.functions),
            nameToId: new Map(this.registry.nameToId),
            aliases: new Map(this.registry.aliases)
        };
    }

    // Clear all functions
    clear(): void {
        this.registry.functions.clear();
        this.registry.nameToId.clear();
        this.registry.aliases.clear();
        this.notifyListeners();
    }

    // Export registry to JSON
    exportToJSON(): string {
        const functions = Array.from(this.registry.functions.values()).map(func => ({
            ...func,
            implementation: func.implementation.toString() // Convert function to string
        }));
        return JSON.stringify(functions, null, 2);
    }

    // Import registry from JSON
    importFromJSON(json: string): void {
        try {
            const functions = JSON.parse(json);
            this.clear();
            
            functions.forEach((funcData: any) => {
                // Reconstruct function from string
                const implementation = new Function('return ' + funcData.implementation)();
                
                const functionDef: FunctionDefinition = {
                    ...funcData,
                    implementation
                };
                
                this.registerFunction(functionDef);
            });
        } catch (error) {
            throw new Error(`Failed to import registry: ${error}`);
        }
    }
}

// Create singleton instance
export const functionRegistry = new FunctionRegistryManager();

// Export the singleton instance
export default functionRegistry; 